import { createSignal } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { chartOfAccountSettingRepository } from '~/repositories/settings/chartOfAccountSettingsRepository';
import type { Accessor } from 'solid-js';
import type { LeaseChartOfAccountSettings } from '~/swagger/settings/Api';

interface ChartOfAccountSettingsContextValue {
  addChartOfAccountSettings: (payload: LeaseChartOfAccountSettings) => Promise<void>;
  addChartOfAccountSettingsByLevel: (level: string, levelId: string, payload: LeaseChartOfAccountSettings) => Promise<void>;
  getChartOfAccountSettings: () => Promise<LeaseChartOfAccountSettings | undefined>;
  getChartOfAccountSettingsByLevel: (level: string, levelId: string) => Promise<LeaseChartOfAccountSettings | undefined>;
  getAllChartOfAccountSettings: () => Promise<LeaseChartOfAccountSettings | undefined>;
  isLoading: Accessor<boolean>;
  error: Accessor<Error | undefined>;
  chartOfAccountSettings: Accessor<LeaseChartOfAccountSettings | undefined>;
}

export const [ChartOfAccountSettingsProvider, useChartOfAccountSettings] = createMagicDoorContext<ChartOfAccountSettingsContextValue>(
  'ChartOfAccountSettings',
  () => {
    const repo = new chartOfAccountSettingRepository();
    const [chartOfAccountSettings, setChartOfAccountSettings] = createSignal<LeaseChartOfAccountSettings | undefined>(undefined);
    const [isLoading, setIsLoading] = createSignal<boolean>(false);
    const [error, setError] = createSignal<Error | undefined>(undefined);

    const addChartOfAccountSettings = async (payload: LeaseChartOfAccountSettings) => {
      setIsLoading(true);
      try {
        await repo.addChartOfAccountSettings(payload);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    const addChartOfAccountSettingsByLevel = async (level: string, levelId: string, payload: LeaseChartOfAccountSettings) => {
      setIsLoading(true);
      try {
        await repo.addChartOfAccountSettingsByLevel(level, levelId, payload);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    const getChartOfAccountSettings = async (): Promise<LeaseChartOfAccountSettings | undefined> => {
      setIsLoading(true);
      try {
        const result = await repo.getChartOfAccountSettings();
        setChartOfAccountSettings(result);
        return result;
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    const getChartOfAccountSettingsByLevel = async (level: string, levelId: string): Promise<LeaseChartOfAccountSettings | undefined> => {
      setIsLoading(true);
      try {
        const result = await repo.getChartOfAccountSettingsByLevel(level, levelId);
        setChartOfAccountSettings(result);
        return result;
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    const getAllChartOfAccountSettings = async (): Promise<LeaseChartOfAccountSettings | undefined> => {
      setIsLoading(true);
      try {
        const result = await repo.getAllChartOfAccountSettings();
        setChartOfAccountSettings(result);
        return result;
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    return {
      addChartOfAccountSettings,
      addChartOfAccountSettingsByLevel,
      getChartOfAccountSettings,
      getChartOfAccountSettingsByLevel,
      getAllChartOfAccountSettings,
      isLoading,
      chartOfAccountSettings,
      error,
    };
  }
);
